import {
  ExpressionSpecification,
  LayerSpecification,
  MapOptions,
  RasterDEMSourceSpecification,
} from 'mapbox-gl';
import {
  canvasColors,
  LOCATION_PIN_DEFAULT_OPACITY,
  LOCATION_PIN_ICON_ID,
  LOCATION_PIN_ICON_NATIVE_SIZE,
  LOCATION_PIN_SIZE_PX,
} from '../map.constants';
import {
  EMPTY_BUILDING_HIT_TARGET_PROPERTY,
  MapStoreyProperty,
  PointIconId,
} from '../models/map-feature.models';

/**
 * Source ID for the building source. Used to reference the building source in the layers.
 */
export const BUILDING_SOURCE_ID = 'map-building-source';

export enum SourceId {
  Building = 'map-building-source',
  Points = 'map-building-points-source',
  PointsHit = 'map-building-points-hit-source',

  DraftLine = 'map-draft-line-source',
  DraftPoints = 'map-draft-points-source',
  LocationPin = 'map-location-pin-source',
  /** Static footprints of non-selected buildings (DEV-2576). */
  OtherBuildings = 'map-other-buildings-source',
}

/**
 * Layer IDs for the map layers. Used to reference the layers in the map.
 */
export enum MapLayerIds {
  BuildingExtrusion = 'map-building-extrusion',
  BuildingRoofLine = 'map-building-roof-line',
  BuildingExtrusionTransparent = 'map-building-extrusion-transparent',
  BuildingFill2d = 'map-building-fill2d',
  Line = 'map-building-line',
  /** Static fill for non-selected building footprints (DEV-2576). */
  OtherBuildingsFill = 'map-other-buildings-fill',
  /** Static outline for non-selected building footprints (DEV-2576). */
  OtherBuildingsLine = 'map-other-buildings-line',
  Points = 'map-building-points',
  PointsHit = 'map-building-points-hit',
  DraftLine = 'map-draft-shape-line',

  DraftPoints = 'map-draft-shape-points',
  LocationPin = 'map-location-pin',
}

/** Custom overlay layer ids used by the design three.js overlay. */
export const MAP_THREEJS_LAYER_IDS = {
  facadeSky: 'map-threejs-facade-sky',
  pointDragGuides: 'map-threejs-point-drag-guides',
  extrusion: 'map-threejs-extrusion',
  wireframe: 'map-threejs-wireframe',
  points: 'map-threejs-points',
  silhouette: 'map-threejs-silhouette',
} as const;

/**
 * Order of layers to be added to the map. First layer is in the bottom and will be covered by the layers below.
 */
export const mapLayerOrder = [
  MapLayerIds.OtherBuildingsFill,
  MapLayerIds.OtherBuildingsLine,
  MapLayerIds.BuildingFill2d,
  MapLayerIds.Points,
  MapLayerIds.DraftPoints,
  MapLayerIds.LocationPin,
];

/** Building overlays hidden while the location pin replaces them. */
export const PIN_MODE_HIDDEN_LAYER_IDS: MapLayerIds[] = [
  MapLayerIds.BuildingFill2d,
  MapLayerIds.Line,
  MapLayerIds.OtherBuildingsFill,
  MapLayerIds.OtherBuildingsLine,
  MapLayerIds.Points,
  MapLayerIds.DraftLine,
  MapLayerIds.DraftPoints,
];

/**
 * Opacity for other-building fill/line. Empty hit-target squares stay at 0
 * so they can still be clicked without looking like a footprint.
 */
export const otherBuildingsLayerOpacity = (
  opacity: number,
): ExpressionSpecification => [
  'case',
  ['==', ['get', EMPTY_BUILDING_HIT_TARGET_PROPERTY], true],
  0,
  opacity,
];

const buildingFill2dLayer: LayerSpecification = {
  id: MapLayerIds.BuildingFill2d,
  type: 'fill',
  source: BUILDING_SOURCE_ID,
  paint: {
    'fill-color': canvasColors.shapeFill,
    'fill-opacity': 0,
  },
};

// Static footprints of non-selected buildings (DEV-2576). Matches the drawn
// polygon look from the ticket: light fill + grey outline. Empty-building
// click targets stay in the source for hit-testing but are not painted.
const otherBuildingsFillLayer: LayerSpecification = {
  id: MapLayerIds.OtherBuildingsFill,
  type: 'fill',
  source: SourceId.OtherBuildings,
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0.05)',
    'fill-opacity': otherBuildingsLayerOpacity(1),
  },
};

const otherBuildingsLineLayer: LayerSpecification = {
  id: MapLayerIds.OtherBuildingsLine,
  type: 'line',
  source: SourceId.OtherBuildings,
  paint: {
    'line-color': '#9e9e9e',
    'line-width': 1,
    'line-opacity': otherBuildingsLayerOpacity(1),
  },
};

// TODO: This is currently used for events but is quite redundant. Remove and ensure events still is working
const pointLayer: LayerSpecification = {
  id: MapLayerIds.Points,
  type: 'symbol',
  source: SourceId.Points,
  layout: {
    'icon-image': [
      'case',
      ['==', ['get', MapStoreyProperty.Selected], true],
      PointIconId.Selected,
      PointIconId.Default,
    ],
    'icon-allow-overlap': false,
    'icon-ignore-placement': true,
    'symbol-elevation-reference': 'ground',
    // 'symbol-z-elevate': true,
  },
  paint: {
    // Henrik: keep the layer for hit-testing + drag, but render points via the ThreeJS custom layer.
    'icon-opacity': 0,
    'symbol-z-offset': [
      'case',
      ['has', MapStoreyProperty.Elevation],
      ['get', MapStoreyProperty.Elevation],
      0,
    ],
  },
};

const draftPointsLayer: LayerSpecification = {
  ...pointLayer,
  id: MapLayerIds.DraftPoints,
  source: SourceId.DraftPoints,
};

const LOCATION_PIN_ICON_SCALE =
  LOCATION_PIN_SIZE_PX / LOCATION_PIN_ICON_NATIVE_SIZE;

const locationPinLayer: LayerSpecification = {
  id: MapLayerIds.LocationPin,
  type: 'symbol',
  source: SourceId.LocationPin,
  layout: {
    visibility: 'none',
    'icon-image': LOCATION_PIN_ICON_ID,
    'icon-size': LOCATION_PIN_ICON_SCALE,
    'icon-anchor': 'bottom',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-pitch-alignment': 'viewport',
    'icon-rotation-alignment': 'viewport',
    'symbol-placement': 'point',
    'symbol-elevation-reference': 'ground',
  },
  paint: {
    'icon-opacity': LOCATION_PIN_DEFAULT_OPACITY,
  },
};

export const mapLayers = {
  // [MapLayerIds.BuildingExtrusionTransparent]: buildingExtrusionTransparentLayer,
  [MapLayerIds.OtherBuildingsFill]: otherBuildingsFillLayer,
  [MapLayerIds.OtherBuildingsLine]: otherBuildingsLineLayer,
  [MapLayerIds.BuildingFill2d]: buildingFill2dLayer,
  [MapLayerIds.Points]: pointLayer,
  [MapLayerIds.DraftPoints]: draftPointsLayer,
  [MapLayerIds.LocationPin]: locationPinLayer,
  // [MapLayerIds.PointsHit]: pointHitLayer,
} as const; // satisfies Record<keyof typeof MapLayerIds, LayerSpecification>;

export const TERRAIN_SOURCE_ID = 'map-terrain-source';

export const terrainSource: RasterDEMSourceSpecification = {
  type: 'raster-dem',
  url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
};

/**
 * Ensures canvas outlines are removed. Called when map is initialized.
 * The canvas itself is focusable (mapbox sets `tabindex`) and we focus it
 * programmatically after map interactions, so its focus ring would otherwise
 * draw a border around the whole map view.
 */
const ensureCanvasOutlineRemoved = (): void => {
  const styleId = 'map-canvas-outline-remove';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .mapboxgl-canvas-container:focus,
    .mapboxgl-canvas-container:focus-visible,
    .mapboxgl-canvas-container:focus-within,
    .mapboxgl-canvas:focus,
    .mapboxgl-canvas:focus-visible {
      outline: none !important;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Default map configuration. Used to initialize the map.
 */
export const DEFAULT_MAP_FOV = 19.5;
/** Mapbox minimum; visually equivalent to an orthographic/isometric camera. */
export const ISOMETRIC_MAP_FOV = 0.01;

export const mapConfigDefaults: Omit<MapOptions, 'container'> = {
  style: 'mapbox://styles/mapbox/standard',
  projection: 'mercator',
  attributionControl: false,
  config: {
    basemap: {
      theme: 'monochrome',
      showPointOfInterestLabels: false,
      showRoadLabels: false,
      showBuildingLabels: false,
      showWaterLabels: false,
      showLanduseLabels: false,
      showLandmarkIcons: false,
      showNaturalLabels: false,
      showPoiLabels: false,
      showPlaceLabels: false,
      showTransitLabels: false,
      showStreetLabels: false,
      showLandmarkIconLabels: false,
      fuelingStationModePointOfInterestLabels: false,
      showHouseNumberLabels: false,
    },
  },

  center: [0, 0],
  zoom: 18,
  pitch: 60,
  bearing: -17.6,
  antialias: true,
};

/**
 * Applies styling to prevent canvas container from showing focus outline.
 * Should be called after map is created and canvas container is available.
 */
export const applyCanvasOutlineRemoval = (map: mapboxgl.Map): void => {
  ensureCanvasOutlineRemoved();
  const canvasContainer = map.getCanvasContainer();
  if (canvasContainer instanceof HTMLElement) {
    canvasContainer.setAttribute('tabindex', '-1');
    canvasContainer.style.outline = 'none';
  }
  const canvas = map.getCanvas();
  if (canvas instanceof HTMLElement) {
    canvas.style.outline = 'none';
  }
};

export const applyLayers = (map: mapboxgl.Map) => {
  for (const layer of Object.values(mapLayers)) {
    if (map.getLayer(layer.id) || !map.getSource(layer.source)) continue;
    if (!map.getSource(layer.source)) continue;
    map.addLayer(layer);
  }
};
